/**
 * Hook for incident chat functionality.
 * 
 * Provides:
 * - WebSocket connection for real-time messaging
 * - Message history fetching
 * - Sending messages and files
 * - Typing indicators
 * - Read receipts
 * 
 * ARCHITECTURE NOTES:
 * - Uses connection ID pattern to handle React Strict Mode double-invocation
 * - WebSocket connection is delayed to skip Strict Mode's first unmount cycle
 * - All async operations check if their connection ID is still current
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@crm/utils';
import { getApiClient, getApiBaseUrl } from '../client';
import type {
    ChatMessage,
    ChatMessagesResponse,
    ChatParticipant,
    IncidentChat,
    TypingPayload,
} from '../types/chat';

/** Query keys for chat */
export const chatKeys = {
    all: ['chat'] as const,
    chat: (incidentId: string) => [...chatKeys.all, incidentId] as const,
    messages: (incidentId: string) => [...chatKeys.chat(incidentId), 'messages'] as const,
    participants: (incidentId: string) => [...chatKeys.chat(incidentId), 'participants'] as const,
};

/** WebSocket connection state */
export type ChatConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseChatOptions {
    /** Enable auto-reconnect on disconnect */
    autoReconnect?: boolean;
    /** Reconnect interval in ms */
    reconnectInterval?: number;
    /** Maximum reconnect attempts */
    maxReconnectAttempts?: number;
}

interface UseChatReturn {
    /** Chat info with participants */
    chat: IncidentChat | undefined;
    /** Message history */
    messages: ChatMessage[];
    /** Loading state for initial fetch */
    isLoading: boolean;
    /** Has more messages to load */
    hasMore: boolean;
    /** WebSocket connection state */
    connectionState: ChatConnectionState;
    /** Users currently typing */
    typingUsers: TypingPayload[];
    /** Send a text message */
    sendMessage: (content: string) => void;
    /** Send a file */
    sendFile: (file: File) => Promise<void>;
    /** Set typing indicator */
    setTyping: (isTyping: boolean) => void;
    /** Mark messages as read */
    markRead: () => void;
    /** Load more messages (older) */
    loadMore: () => void;
    /** Reconnect WebSocket */
    reconnect: () => void;
}

// Generate unique connection ID
let connectionIdCounter = 0;
function generateConnectionId(): number {
    return ++connectionIdCounter;
}

/**
 * Main chat hook for incident messaging.
 * 
 * Handles React 18 Strict Mode properly by using connection IDs
 * and delayed connection to survive the mount-unmount-remount cycle.
 */
export function useChat(
    incidentId: string,
    options: UseChatOptions = {}
): UseChatReturn {
    const {
        autoReconnect = true,
        reconnectInterval = 3000,
        maxReconnectAttempts = 5,
    } = options;

    const queryClient = useQueryClient();
    
    // Use refs for mutable state that shouldn't trigger re-renders
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const connectDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    
    // Connection ID pattern: only the latest connection attempt should proceed
    const currentConnectionIdRef = useRef<number>(0);

    // State
    const [connectionState, setConnectionState] = useState<ChatConnectionState>('disconnected');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [typingUsers, setTypingUsers] = useState<TypingPayload[]>([]);

    // Typing timeout - auto-clear after 3s
    const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    // Fetch chat info
    const { data: chat, isLoading: chatLoading } = useQuery({
        queryKey: chatKeys.chat(incidentId),
        queryFn: async () => {
            const client = getApiClient();
            const response = await client.get<IncidentChat>(`/api/v1/incidents/${incidentId}/chat/`);
            return response.data;
        },
        enabled: !!incidentId,
    });

    // Fetch initial messages
    const { data: messagesData, isLoading: messagesLoading } = useQuery({
        queryKey: chatKeys.messages(incidentId),
        queryFn: async () => {
            const client = getApiClient();
            const response = await client.get<ChatMessagesResponse>(
                `/api/v1/incidents/${incidentId}/chat/messages/`
            );
            return response.data;
        },
        enabled: !!incidentId,
        // Always refetch on mount to get fresh messages
        staleTime: 0,
    });

    // Sync messages from query data to state
    // This handles both initial load AND when returning to the page with cached data
    useEffect(() => {
        if (messagesData) {
            setMessages(messagesData.messages);
            setHasMore(messagesData.has_more);
        }
    }, [messagesData]);

    // Build WebSocket URL
    const getWsUrl = useCallback(() => {
        const token = getAccessToken();
        
        // Get API URL from initialized client (e.g., "https://itcrmapi.duckdns.org")
        const apiUrl = getApiBaseUrl();
        
        // In development (Vite ports 5173/5174), use localhost:8001 for Daphne
        const isDev = window.location.port.startsWith('517');
        
        if (isDev) {
            // Development: explicit port 8001 for Daphne WebSocket server
            const host = window.location.hostname;
            return `ws://${host}:8001/ws/chat/${incidentId}/?token=${token}`;
        }
        
        // Production: derive WebSocket URL from API base URL
        if (apiUrl) {
            try {
                const url = new URL(apiUrl);
                const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
                // Use the API host for WebSocket (e.g., itcrmapi.duckdns.org, not itcrmworker.duckdns.org)
                const portPart = url.port ? `:${url.port}` : '';
                return `${wsProtocol}//${url.hostname}${portPart}/ws/chat/${incidentId}/?token=${token}`;
            } catch (e) {
                console.error('[Chat WebSocket] Failed to parse API URL:', apiUrl, e);
            }
        }
        
        // Fallback: use current window location (may not work for cross-origin setups)
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.hostname;
        console.warn('[Chat WebSocket] Using fallback URL - API base URL not available');
        return `${protocol}//${host}/ws/chat/${incidentId}/?token=${token}`;
    }, [incidentId]);

    // Handle incoming WebSocket messages
    const handleWsMessage = useCallback((data: { type: string; payload: unknown }) => {
        switch (data.type) {
            case 'chat.message': {
                const message = data.payload as ChatMessage;
                setMessages(prev => [...prev, message]);
                break;
            }

            case 'chat.typing': {
                const typing = data.payload as TypingPayload;

                if (typing.is_typing) {
                    setTypingUsers(prev => {
                        const exists = prev.some(u => u.user_id === typing.user_id);
                        if (exists) return prev;
                        return [...prev, typing];
                    });

                    // Clear after timeout
                    const existingTimeout = typingTimeoutsRef.current.get(typing.user_id);
                    if (existingTimeout) clearTimeout(existingTimeout);

                    const timeout = setTimeout(() => {
                        setTypingUsers(prev => prev.filter(u => u.user_id !== typing.user_id));
                        typingTimeoutsRef.current.delete(typing.user_id);
                    }, 3000);

                    typingTimeoutsRef.current.set(typing.user_id, timeout);
                } else {
                    setTypingUsers(prev => prev.filter(u => u.user_id !== typing.user_id));
                    const timeout = typingTimeoutsRef.current.get(typing.user_id);
                    if (timeout) clearTimeout(timeout);
                    typingTimeoutsRef.current.delete(typing.user_id);
                }
                break;
            }

            case 'chat.read': {
                // Could update read receipts UI
                break;
            }

            case 'chat.participant_added':
            case 'chat.participant_removed': {
                queryClient.invalidateQueries({ queryKey: chatKeys.chat(incidentId) });
                break;
            }

            case 'pong':
                // Heartbeat response - no action needed
                break;
        }
    }, [incidentId, queryClient]);

    // Close existing WebSocket and cleanup
    const closeWebSocket = useCallback(() => {
        if (wsRef.current) {
            // Prevent further event handlers from running
            wsRef.current.onopen = null;
            wsRef.current.onclose = null;
            wsRef.current.onerror = null;
            wsRef.current.onmessage = null;
            
            if (wsRef.current.readyState === WebSocket.OPEN || 
                wsRef.current.readyState === WebSocket.CONNECTING) {
                wsRef.current.close(1000, 'Component cleanup');
            }
            wsRef.current = null;
        }
    }, []);

    // Connect to WebSocket with connection ID tracking
    const connect = useCallback((connectionId: number) => {
        // If this isn't the current connection attempt, abort
        if (connectionId !== currentConnectionIdRef.current) {
            return;
        }

        // Clean up any existing connection
        closeWebSocket();

        setConnectionState('connecting');

        const wsUrl = getWsUrl();
        console.debug('[Chat] Connecting to WebSocket:', wsUrl.replace(/token=.*/, 'token=***'));
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            // Verify this is still the current connection
            if (connectionId !== currentConnectionIdRef.current) {
                ws.close(1000, 'Stale connection');
                return;
            }
            console.debug('[Chat] WebSocket connected');
            setConnectionState('connected');
            reconnectAttempts.current = 0;
        };

        ws.onclose = (event) => {
            // Verify this is still the current connection
            if (connectionId !== currentConnectionIdRef.current) {
                return;
            }
            
            console.debug('[Chat] WebSocket closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
            wsRef.current = null;
            setConnectionState('disconnected');

            // Auto-reconnect if enabled and not a normal close
            // Don't reconnect on auth failures (4001, 4003) or if max attempts reached
            const isAuthError = event.code === 4001 || event.code === 4003;
            const shouldReconnect = autoReconnect && 
                event.code !== 1000 && 
                !isAuthError && 
                reconnectAttempts.current < maxReconnectAttempts;
            
            if (shouldReconnect) {
                // Exponential backoff: 3s, 6s, 12s, 24s, 48s
                const backoff = reconnectInterval * Math.pow(2, reconnectAttempts.current);
                console.debug(`[Chat] Reconnecting in ${backoff}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
                
                reconnectTimerRef.current = setTimeout(() => {
                    // Check again before reconnecting
                    if (connectionId === currentConnectionIdRef.current) {
                        reconnectAttempts.current++;
                        connect(connectionId);
                    }
                }, backoff);
            } else if (isAuthError) {
                console.error('[Chat] Authentication failed, not reconnecting');
                setConnectionState('error');
            }
        };

        ws.onerror = (event) => {
            // Verify this is still the current connection
            if (connectionId !== currentConnectionIdRef.current) {
                return;
            }
            console.error('[Chat] WebSocket error:', event);
            setConnectionState('error');
        };

        ws.onmessage = (event) => {
            // Verify this is still the current connection
            if (connectionId !== currentConnectionIdRef.current) {
                return;
            }
            try {
                const data = JSON.parse(event.data);
                handleWsMessage(data);
            } catch (e) {
                console.error('[Chat] Failed to parse WebSocket message:', e);
            }
        };
    }, [getWsUrl, handleWsMessage, closeWebSocket, autoReconnect, reconnectInterval, maxReconnectAttempts]);

    // Connect on mount - with delay to handle React Strict Mode
    useEffect(() => {
        // Only connect when we have the chat data (confirms participant access)
        if (!incidentId || !chat) {
            return;
        }

        // Generate new connection ID for this effect instance
        const connectionId = generateConnectionId();
        currentConnectionIdRef.current = connectionId;

        // Delay connection slightly to survive React Strict Mode's
        // mount → unmount → remount cycle. The first unmount happens
        // within the same microtask, so a small delay skips it.
        connectDelayTimerRef.current = setTimeout(() => {
            // Verify this connection ID is still current
            if (connectionId === currentConnectionIdRef.current) {
                connect(connectionId);
            }
        }, 50);

        return () => {
            // Invalidate this connection ID
            if (currentConnectionIdRef.current === connectionId) {
                currentConnectionIdRef.current = 0;
            }

            // Clear timers
            if (connectDelayTimerRef.current) {
                clearTimeout(connectDelayTimerRef.current);
                connectDelayTimerRef.current = null;
            }
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            // Close WebSocket
            closeWebSocket();
        };
    }, [incidentId, chat, connect, closeWebSocket]);

    // Cleanup typing timeouts on unmount
    useEffect(() => {
        return () => {
            typingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            typingTimeoutsRef.current.clear();
        };
    }, []);

    // Send text message
    const sendMessage = useCallback((content: string) => {
        if (!content.trim()) return;
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket not connected, cannot send message');
            return;
        }

        wsRef.current.send(JSON.stringify({
            action: 'send_message',
            content: content.trim(),
        }));
    }, []);

    // Send file (via REST API)
    const sendFileMutation = useMutation({
        mutationFn: async (file: File) => {
            const client = getApiClient();
            const formData = new FormData();
            formData.append('attachment', file);
            formData.append('message_type', file.type.startsWith('image/') ? 'image' : 'file');
            formData.append('content', '');

            const response = await client.post<ChatMessage>(
                `/api/v1/incidents/${incidentId}/chat/messages/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        },
    });

    const sendFile = useCallback(async (file: File) => {
        await sendFileMutation.mutateAsync(file);
    }, [sendFileMutation]);

    // Set typing indicator
    const setTyping = useCallback((isTyping: boolean) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        wsRef.current.send(JSON.stringify({
            action: 'typing',
            is_typing: isTyping,
        }));
    }, []);

    // Mark messages as read
    const markRead = useCallback(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        wsRef.current.send(JSON.stringify({
            action: 'mark_read',
        }));
    }, []);

    // Load more messages (pagination)
    const loadMore = useCallback(async () => {
        if (!hasMore || messages.length === 0) return;

        const client = getApiClient();
        const oldestId = messages[0].id;
        const response = await client.get<ChatMessagesResponse>(
            `/api/v1/incidents/${incidentId}/chat/messages/`,
            { params: { before: oldestId } }
        );

        setMessages(prev => [...response.data.messages, ...prev]);
        setHasMore(response.data.has_more);
    }, [incidentId, hasMore, messages]);

    // Manual reconnect
    const reconnect = useCallback(() => {
        reconnectAttempts.current = 0;
        
        // Generate new connection ID and connect
        const connectionId = generateConnectionId();
        currentConnectionIdRef.current = connectionId;
        connect(connectionId);
    }, [connect]);

    return {
        chat,
        messages,
        isLoading: chatLoading || messagesLoading,
        hasMore,
        connectionState,
        typingUsers,
        sendMessage,
        sendFile,
        setTyping,
        markRead,
        loadMore,
        reconnect,
    };
}

/** Fetch chat participants */
export function useChatParticipants(incidentId: string) {
    return useQuery({
        queryKey: chatKeys.participants(incidentId),
        queryFn: async () => {
            const client = getApiClient();
            const response = await client.get<ChatParticipant[]>(
                `/api/v1/incidents/${incidentId}/chat/participants/`
            );
            return response.data;
        },
        enabled: !!incidentId,
    });
}

/** Add participant to chat */
export function useAddChatParticipant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ incidentId, userId, role }: { incidentId: string; userId: string; role?: string }) => {
            const client = getApiClient();
            const response = await client.post<ChatParticipant>(
                `/api/v1/incidents/${incidentId}/chat/participants/`,
                { user_id: userId, role }
            );
            return response.data;
        },
        onSuccess: (_, { incidentId }) => {
            queryClient.invalidateQueries({ queryKey: chatKeys.chat(incidentId) });
        },
    });
}

/** Remove participant from chat */
export function useRemoveChatParticipant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ incidentId, userId }: { incidentId: string; userId: string }) => {
            const client = getApiClient();
            await client.delete(`/api/v1/incidents/${incidentId}/chat/participants/${userId}/`);
        },
        onSuccess: (_, { incidentId }) => {
            queryClient.invalidateQueries({ queryKey: chatKeys.chat(incidentId) });
        },
    });
}

